<script>
    import {
        pedalState,
        PEDAL_SOURCES,
        togglePedalControl,
        selectDeviceByIndex,
        setPedalSource,
        setSelectedGamepadIndex,
        setFunctionMapping,
        setGamepadMode,
        setGamepadButtonMapping,
        savePedalSettings,
        refreshHidDevices,
        refreshGamepads,
        startPedalControl,
    } from "@app/store/pedalStore.svelte.js";
    import * as m from "@app/paraglide/messages.js";

    const mappingValues = [0, 1, 2, 3, 4];
    const gamepadButtonValues = [0, 1, 2, 3];

    async function onEnableChange(e) {
        const enabled = e.currentTarget.checked;
        try {
            await togglePedalControl(enabled, {
                requestPermission: pedalState.source === PEDAL_SOURCES.WEBHID,
            });
        } catch (err) {
            console.error(err);
        }
    }

    async function onSourceChange(e) {
        const source = e.currentTarget.value;
        await setPedalSource(source);
        if (source === PEDAL_SOURCES.GAMEPAD) {
            refreshGamepads();
        }
        if (source === PEDAL_SOURCES.WEBHID) {
            await refreshHidDevices();
        }
    }

    async function onHidDeviceChange(e) {
        const index = Number(e.currentTarget.value);
        await selectDeviceByIndex(index);
    }

    async function onGamepadChange(e) {
        const value = e.currentTarget.value;
        const index = value === "" ? null : Number(value);
        await setSelectedGamepadIndex(index);
    }

    function onMappingChange(value, e) {
        setFunctionMapping(value, e.currentTarget.value);
    }

    async function onGamepadModeChange(e) {
        await setGamepadMode(e.currentTarget.value);
    }

    function onGamepadButtonMappingChange(buttonIndex, e) {
        setGamepadButtonMapping(buttonIndex, e.currentTarget.value);
    }

    async function requestHidAgain() {
        try {
            await togglePedalControl(true, { requestPermission: true });
        } catch (err) {
            console.error(err);
        }
    }

    async function refreshGamepadList() {
        refreshGamepads();
        if (pedalState.enable && pedalState.source === PEDAL_SOURCES.GAMEPAD) {
            try {
                await startPedalControl();
            } catch (err) {
                console.error(err);
            }
        }
        savePedalSettings();
    }
</script>

<h5>{m.options_pedal_title()}</h5>
<form id="footPedalForm" aria-label={m.options_pedal_form_label()}>
    <div class="my-4 form-check form-switch">
        <input
            id="footPedalEnabledCheck"
            type="checkbox"
            class="form-check-input"
            checked={pedalState.enable}
            onchange={onEnableChange}
            aria-labelledby="footPedalEnabledLabel"
        />
        <label id="footPedalEnabledLabel" class="form-check-label" for="footPedalEnabledCheck">
            {m.options_pedal_enable()}
            <i class="bi bi-info-circle" title={m.options_pedal_enable_help()}></i>
        </label>
    </div>

    <div class="mb-3">
        <label id="footPedalSourceLabel" for="footPedalSourceSelect" class="form-label">
            {m.options_pedal_input_source()}
        </label>
        <select
            id="footPedalSourceSelect"
            class="form-select"
            value={pedalState.source}
            onchange={onSourceChange}
            aria-labelledby="footPedalSourceLabel"
            disabled={!pedalState.enable}
        >
            <option value={PEDAL_SOURCES.WEBHID}>{m.options_pedal_source_webhid()}</option>
            <option value={PEDAL_SOURCES.VPEDAL}>{m.options_pedal_source_vpedal()}</option>
            <option value={PEDAL_SOURCES.GAMEPAD}>{m.options_pedal_source_gamepad()}</option>
        </select>
        <div class="form-text">{m.options_pedal_hold_note()}</div>
    </div>

    {#if pedalState.error}
        <div class="alert alert-warning py-2" role="alert">{pedalState.error}</div>
    {/if}

    {#if pedalState.source === PEDAL_SOURCES.WEBHID}
        <div class="mb-3">
            <label id="footPedalInputSelectLabel" for="footPedaliInputDeviceSelect" class="form-label">
                {m.options_pedal_input_device()}
            </label>
            <div class="d-flex gap-2">
                <select
                    id="footPedaliInputDeviceSelect"
                    class="form-select"
                    value={pedalState.inputDevices.findIndex(
                        (d) => d === pedalState.selectedInput,
                    )}
                    onchange={onHidDeviceChange}
                    aria-labelledby="footPedalInputSelectLabel"
                    disabled={!pedalState.enable}
                >
                    {#if pedalState.inputDevices.length === 0}
                        <option value="-1">{m.options_pedal_no_devices()}</option>
                    {/if}
                    {#each pedalState.inputDevices as inputDevice, i (inputDevice.productId)}
                        <option value={i}>{inputDevice.productName}</option>
                    {/each}
                </select>
                <button
                    type="button"
                    class="btn btn-outline-secondary text-nowrap"
                    disabled={!pedalState.enable}
                    onclick={requestHidAgain}
                >
                    {m.options_pedal_request_device()}
                </button>
            </div>
        </div>

        <h5>{m.options_pedal_function_mapping_title()}</h5>
        <p class="text-muted small">{m.options_pedal_function_mapping_desc()}</p>
        <table class="table">
            <thead>
                <tr>
                    <th scope="col">{m.options_pedal_value_header()}</th>
                    <th scope="col">{m.options_pedal_action_header()}</th>
                </tr>
            </thead>
            <tbody>
                {#each mappingValues as value (value)}
                    <tr>
                        <td>{value}</td>
                        <td>
                            <select
                                class="form-select"
                                value={pedalState.functionMapping[value]}
                                onchange={(e) => onMappingChange(value, e)}
                                disabled={!pedalState.enable}
                            >
                                {#if value === 0}
                                    <option value="pause">{m.options_pedal_action_pause()}</option>
                                    <option value="play">{m.options_pedal_action_play()}</option>
                                    <option value="rewind">{m.options_pedal_action_rewind()}</option>
                                    <option value="fastForward"
                                        >{m.options_pedal_action_fast_forward()}</option
                                    >
                                    <option value="none">{m.options_pedal_action_none()}</option>
                                {:else}
                                    <option value="play">{m.options_pedal_action_play()}</option>
                                    <option value="rewind">{m.options_pedal_action_rewind()}</option>
                                    <option value="fastForward"
                                        >{m.options_pedal_action_fast_forward()}</option
                                    >
                                    <option value="pause">{m.options_pedal_action_pause()}</option>
                                    <option value="none">{m.options_pedal_action_none()}</option>
                                {/if}
                            </select>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {:else if pedalState.source === PEDAL_SOURCES.VPEDAL}
        <div class="alert alert-info" role="status">
            <p class="mb-2 fw-semibold">{m.options_pedal_vpedal_setup_title()}</p>
            <ol class="mb-2 ps-3">
                <li>{m.options_pedal_vpedal_step_install()}</li>
                <li>{m.options_pedal_vpedal_step_run()}</li>
                <li>{m.options_pedal_vpedal_step_focus()}</li>
            </ol>
            <p class="mb-0 small">{m.options_pedal_vpedal_mapping_note()}</p>
        </div>
    {:else if pedalState.source === PEDAL_SOURCES.GAMEPAD}
        <div class="mb-3">
            <label id="footPedalGamepadLabel" for="footPedalGamepadSelect" class="form-label">
                {m.options_pedal_gamepad_device()}
            </label>
            <div class="d-flex gap-2">
                <select
                    id="footPedalGamepadSelect"
                    class="form-select"
                    value={pedalState.selectedGamepadIndex ?? ""}
                    onchange={onGamepadChange}
                    aria-labelledby="footPedalGamepadLabel"
                    disabled={!pedalState.enable}
                >
                    {#if pedalState.gamepads.length === 0}
                        <option value="">{m.options_pedal_no_gamepads()}</option>
                    {/if}
                    {#each pedalState.gamepads as pad (pad.index)}
                        <option value={pad.index}>{pad.id}</option>
                    {/each}
                </select>
                <button
                    type="button"
                    class="btn btn-outline-secondary text-nowrap"
                    disabled={!pedalState.enable}
                    onclick={refreshGamepadList}
                >
                    {m.options_pedal_refresh_gamepads()}
                </button>
            </div>
            <div class="form-text">{m.options_pedal_gamepad_mapping_note()}</div>
        </div>

        <div class="mb-3">
            <label id="footPedalGamepadModeLabel" for="footPedalGamepadModeSelect" class="form-label">
                {m.options_pedal_gamepad_mode()}
            </label>
            <select
                id="footPedalGamepadModeSelect"
                class="form-select"
                value={pedalState.gamepadMode}
                onchange={onGamepadModeChange}
                aria-labelledby="footPedalGamepadModeLabel"
                disabled={!pedalState.enable}
            >
                <option value="independent">{m.options_pedal_gamepad_mode_independent()}</option>
                <option value="combo">{m.options_pedal_gamepad_mode_combo()}</option>
            </select>
        </div>

        {#if pedalState.gamepadMode === "independent"}
            <h5>{m.options_pedal_function_mapping_title()}</h5>
            <p class="text-muted small">{m.options_pedal_gamepad_button_mapping_desc()}</p>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col">{m.options_pedal_gamepad_button_header()}</th>
                        <th scope="col">{m.options_pedal_action_header()}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each gamepadButtonValues as buttonIndex (buttonIndex)}
                        <tr>
                            <td>#{buttonIndex}</td>
                            <td>
                                <select
                                    class="form-select"
                                    value={pedalState.gamepadButtonMapping[buttonIndex]}
                                    onchange={(e) => onGamepadButtonMappingChange(buttonIndex, e)}
                                    disabled={!pedalState.enable}
                                >
                                    <option value="play">{m.options_pedal_action_play()}</option>
                                    <option value="rewind">{m.options_pedal_action_rewind()}</option>
                                    <option value="fastForward"
                                        >{m.options_pedal_action_fast_forward()}</option
                                    >
                                    <option value="pause">{m.options_pedal_action_pause()}</option>
                                    <option value="none">{m.options_pedal_action_none()}</option>
                                </select>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/if}
    {/if}
</form>
